// CB:BEGIN admin-homeowners-list
import React, { useState, useEffect } from 'react';
import ResponsiveContainer from '../../components/layout/ResponsiveContainer';
import SettingsCard from '../../components/ui/SettingsCard';
import { FaSearch, FaEdit, FaEye, FaTrash } from 'react-icons/fa';
import { useUser } from '@clerk/clerk-react';
import { useApi } from '@/lib/useApi';
// Removed unused admin homeowner dashboard components
import AdminHomeownerView from './AdminHomeownerView';
import AdminEditHomeownerDashboard from './AdminEditHomeownerDashboard';
import AdminEditHomeownerProfile from './AdminEditHomeownerProfile';
// CB:BEGIN homeowner-documents-import
import HomeownerDocumentsModal from '../../components/admin/HomeownerDocumentsModal';
// CB:END homeowner-documents-import
// Removed unused HomeownerEditForm

interface Homeowner {
  id: string;
  firstName?: string;
  lastName?: string;
  address?: string;
  archived?: boolean;
  user: {
    email: string;
    phone?: string;
  };
  _count: {
    projects: number;
  };
}

interface HomeownersResponse {
  homeowners: Homeowner[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

const AdminHomeownersList: React.FC<{ onNavigate?: (screen: string) => void }> = ({ onNavigate }) => {
  const { user, isLoaded } = useUser();
  const api = useApi();
  const [homeowners, setHomeowners] = useState<Homeowner[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [archiveFilter, setArchiveFilter] = useState<'active' | 'archived' | 'all'>('active');
  const [sortOrder, setSortOrder] = useState<'name' | 'date'>('name');
  const [error, setError] = useState<string | null>(null);
  const [selectedHomeowner, setSelectedHomeowner] = useState<Homeowner | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'view' | 'edit-profile' | 'edit-dashboard'>('list');
  // CB:BEGIN homeowner-documents-state
  const [documentsModalOpen, setDocumentsModalOpen] = useState(false);
  const [selectedHomeownerForDocuments, setSelectedHomeownerForDocuments] = useState<Homeowner | null>(null);
  // CB:END homeowner-documents-state

  useEffect(() => {
    if (isLoaded && user) {
      fetchHomeowners();
    }
  }, [isLoaded, user, archiveFilter]);

  const fetchHomeowners = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      console.log('🔍 Fetching homeowners...');
      const params = new URLSearchParams();
      if (archiveFilter !== 'all') {
        params.append('archive', archiveFilter);
      }
      
      const data = await api.get<HomeownersResponse>(`/api/admin/homeowners?${params.toString()}`);
      console.log('✅ Homeowners data received:', data);
      
      // CB:BEGIN defensive-homeowners-response
      // Handle 403 errors and malformed responses
      if (data && 'error' in data) {
        throw new Error(data.error || 'Forbidden');
      }
      if (!data || !Array.isArray(data.homeowners)) {
        console.warn('⚠️ Invalid homeowners response:', data);
        setHomeowners([]);
        setError('Invalid response format');
        return;
      }
      // CB:END defensive-homeowners-response
      
      setHomeowners(data.homeowners || []);
    } catch (err: any) {
      console.error('❌ Error fetching homeowners:', err);
      const msg = err?.message === 'API_UNREACHABLE'
        ? 'Backend unavailable. Start the API (e.g. npm run dev:backend) and refresh.'
        : (err instanceof Error ? err.message : 'Failed to fetch homeowners');
      setError(msg);
      setHomeowners([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewHomeowner = (homeowner: Homeowner) => {
    console.log('🔍 handleViewHomeowner called for:', homeowner.id);
    setSelectedHomeowner(homeowner);
    setViewMode('view');
  };

  const handleEditHomeownerProfile = (homeowner: Homeowner) => {
    setSelectedHomeowner(homeowner);
    setViewMode('edit-profile');
  };

  const handleEditHomeownerDashboard = (homeowner: Homeowner) => {
    console.log('🔍 handleEditHomeownerDashboard called for:', homeowner.id);
    setSelectedHomeowner(homeowner);
    setViewMode('edit-dashboard');
  };

  const handleDeleteHomeowner = async (homeowner: Homeowner) => {
    const firstName = homeowner.firstName || 'Unknown';
    const lastName = homeowner.lastName || 'User';
    const projectCount = homeowner._count?.projects || 0;
    
    const confirmed = window.confirm(
      `Are you sure you want to PERMANENTLY DELETE homeowner ${firstName} ${lastName}?\n\n` +
      `This will also delete:\n` +
      `• ${projectCount} project${projectCount !== 1 ? 's' : ''}\n` +
      `• All bids associated with those projects\n` +
      `• Their user account\n\n` +
      `This action CANNOT be undone!`
    );
    
    if (!confirmed) {
      return;
    }
    
    try {
      const response = await api.delete(`/api/admin/homeowners/${homeowner.id}`);
      
      // Remove the homeowner from the local state immediately for better UX
      setHomeowners(prev => prev.filter(h => h.id !== homeowner.id));
      
      // Also refresh the data from the server to ensure consistency
      await fetchHomeowners();
      
      console.log(`✅ Homeowner ${firstName} ${lastName} deleted successfully`);
      console.log(`🗑️ Deleted ${response.deletedCounts?.projects || 0} projects and ${response.deletedCounts?.bids || 0} bids`);
    } catch (err) {
      console.error('Error deleting homeowner:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete homeowner');
      // Refresh the list in case of error to ensure we have the latest data
      await fetchHomeowners();
    }
  };

  const handleToggleArchive = async (homeowner: Homeowner) => {
    const newArchivedStatus = !homeowner.archived;
    const statusText = newArchivedStatus ? 'archived' : 'active';
    const firstName = homeowner.firstName || 'Unknown';
    const lastName = homeowner.lastName || 'User';
    
    const confirmed = window.confirm(
      `Are you sure you want to set Homeowner ${firstName} ${lastName} to ${statusText}?`
    );
    
    if (!confirmed) {
      return;
    }
    
    try {
      await api.patch(`/api/admin/homeowners/${homeowner.id}/archive`, {
        archived: newArchivedStatus
      });
      
      // Refresh the data from the server to show the updated homeowner in the correct view
      await fetchHomeowners();
      
      console.log(`✅ Homeowner ${firstName} ${lastName} ${statusText} successfully`);
    } catch (err) {
      console.error('Error toggling archive status:', err);
      setError('Failed to update archive status');
    }
  };

  const handleBackToList = () => {
    setViewMode('list');
    setSelectedHomeowner(null);
    // Refresh the homeowners list to show updated data
    fetchHomeowners();
  };

  // const handleSaved = () => {
  //   setViewMode('view');
  //   fetchHomeowners();
  // };

  const filteredHomeowners = (homeowners || [])
    .filter(homeowner =>
      homeowner.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      homeowner.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      homeowner.user?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      homeowner.address?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      if (sortOrder === 'name') {
        return (a.lastName || '').localeCompare(b.lastName || '');
      }
      const aAt = (a as any).user?.createdAt ?? (a as any).createdAt ?? 0;
      const bAt = (b as any).user?.createdAt ?? (b as any).createdAt ?? 0;
      return new Date(bAt).getTime() - new Date(aAt).getTime();
    });

  const showSpinner = !isLoaded || loading;
  const showAuthError = !user;

  console.log('🔍 AdminHomeownersList render state:', { 
    isLoaded, 
    loading, 
    user: !!user, 
    homeowners: homeowners.length, 
    showSpinner, 
    showAuthError,
    error 
  });

  // Render different views based on mode
  if (viewMode === 'view' && selectedHomeowner) {
    return (
      <AdminHomeownerView 
        homeownerId={selectedHomeowner.id}
        onNavigate={(screen) => {
          if (screen === 'admin-homeowners-list') {
            handleBackToList();
          } else {
            onNavigate?.(screen);
          }
        }}
      />
    );
  }

  if (viewMode === 'edit-profile' && selectedHomeowner) {
    return (
      <AdminEditHomeownerProfile
        homeownerId={selectedHomeowner.id}
        onNavigate={(screen) => {
          if (screen === 'admin-homeowners-list') {
            handleBackToList();
          } else {
            onNavigate?.(screen);
          }
        }}
      />
    );
  }

  if (viewMode === 'edit-dashboard' && selectedHomeowner) {
    return (
      <AdminEditHomeownerDashboard
        homeownerId={selectedHomeowner.id}
        onNavigate={(screen) => {
          if (screen === 'admin-homeowners-list') {
            handleBackToList();
          } else {
            onNavigate?.(screen);
          }
        }}
      />
    );
  }

  return (
    <ResponsiveContainer className="container">
      <div className="content">
        {showAuthError && (
          <div className="text-center py-12 text-red-600">Please sign in to access this page</div>
        )}
        {showSpinner && (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        )}
        {!showAuthError && !showSpinner && (
          <>
            {error && (
              <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: 12, marginBottom: 16, color: '#991B1B' }}>
                {error}
              </div>
            )}
            <div style={{ marginBottom: 32 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h1 style={{ fontSize: 28 }} data-testid="homeowners-title">Homeowners</h1>
                  <p style={{ color: '#6b7280', marginTop: 8 }}>Manage homeowner accounts and project assignments</p>
                </div>
                <button
                  onClick={() => onNavigate?.('admin-homeowner-add')}
                  className="btn btn-primary"
                  style={{ fontSize: 14, padding: '10px 20px' }}
                >
                  Add Homeowner +
                </button>
              </div>
              
              {/* Filter Buttons */}
              <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
                <button
                  onClick={() => setArchiveFilter('active')}
                  className={`btn ${archiveFilter === 'active' ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ fontSize: 12, padding: '6px 12px' }}
                >
                  Show Active
                </button>
                <button
                  onClick={() => setArchiveFilter('archived')}
                  className={`btn ${archiveFilter === 'archived' ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ fontSize: 12, padding: '6px 12px' }}
                >
                  Show Archived
                </button>
                <button
                  onClick={() => setArchiveFilter('all')}
                  className={`btn ${archiveFilter === 'all' ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ fontSize: 12, padding: '6px 12px' }}
                >
                  Show All
                </button>
              </div>
              
              {/* Sort Buttons */}
              <div style={{ marginTop: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ fontSize: 14, color: '#6b7280', marginRight: 8 }}>Sort by:</span>
                <button
                  onClick={() => setSortOrder('name')}
                  className={`btn ${sortOrder === 'name' ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ fontSize: 12, padding: '6px 12px' }}
                >
                  Name (A-Z)
                </button>
                <button
                  onClick={() => setSortOrder('date')}
                  className={`btn ${sortOrder === 'date' ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ fontSize: 12, padding: '6px 12px' }}
                >
                  Date Added
                </button>
              </div>
            </div>

            {/* Search */}
            <SettingsCard title="Search Homeowners">
              <div style={{ position: 'relative' }}>
                <div style={{ position: 'absolute', top: '50%', left: 12, transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                  <FaSearch style={{ width: 20, height: 20, color: '#9ca3af' }} />
                </div>
                <input
                  type="text"
                  placeholder="Search homeowners..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-control"
                  style={{ paddingLeft: 40 }}
                />
              </div>
            </SettingsCard>

            {/* Homeowners List */}
            <SettingsCard title="All Homeowners">
              {filteredHomeowners.length > 0 ? (
                <div className="space-y-4">
                  {filteredHomeowners.map((homeowner) => (
                    <div key={homeowner.id} className="project-card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{ 
                          width: 48, 
                          height: 48, 
                          borderRadius: '50%', 
                          background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          marginRight: 16
                        }}>
                          <span style={{ fontSize: 16, fontWeight: '600', color: 'white' }}>
                            {homeowner.firstName?.[0] || homeowner.user?.email?.[0]?.toUpperCase() || 'H'}
                          </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                          <div>
                            <div style={{ fontSize: 18, fontWeight: '600', color: '#111827', marginBottom: 4 }}>
                              {homeowner.firstName && homeowner.lastName
                                ? `${homeowner.firstName} ${homeowner.lastName}`
                                : homeowner.user?.email
                                  ? `Homeowner (${homeowner.user.email})`
                                  : 'Unnamed Homeowner'
                              }
                            </div>
                            <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 2 }}>{homeowner.user?.email || 'N/A'}</div>
                            {homeowner.address && (
                              <div style={{ fontSize: 14, color: '#9ca3af' }}>{homeowner.address}</div>
                            )}
                          </div>
                          <button
                            onClick={() => handleEditHomeownerProfile(homeowner)}
                            className="btn btn-secondary"
                            style={{ fontSize: 12, padding: '6px 12px', height: 'fit-content' }}
                          >
                            <FaEdit style={{ marginRight: 4 }} />
                            Edit Profile
                          </button>
                          <button
                            onClick={() => handleToggleArchive(homeowner)}
                            className={`btn ${homeowner.archived ? 'btn-secondary' : 'btn-success'}`}
                            style={{ 
                              fontSize: 12, 
                              padding: '6px 12px', 
                              height: 'fit-content',
                              backgroundColor: homeowner.archived ? '#6b7280' : '#10b981',
                              color: 'white',
                              border: 'none'
                            }}
                          >
                            {homeowner.archived ? 'Archived' : 'Active'}
                          </button>
                          <button
                            onClick={() => {
                              setSelectedHomeownerForDocuments(homeowner);
                              setDocumentsModalOpen(true);
                            }}
                            className="btn btn-secondary"
                            style={{ 
                              fontSize: 12, 
                              padding: '6px 12px', 
                              height: 'fit-content'
                            }}
                          >
                            View Documents
                          </button>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 18, fontWeight: 'bold', color: '#111827' }}>{homeowner._count?.projects || 0}</div>
                          <div style={{ fontSize: 12, color: '#6b7280' }}>project{(homeowner._count?.projects || 0) !== 1 ? 's' : ''}</div>
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button
                            onClick={() => handleViewHomeowner(homeowner)}
                            className="btn btn-primary"
                            style={{ fontSize: 12, padding: '6px 12px' }}
                            title="View homeowner dashboard and enter their session"
                          >
                            <FaEye style={{ marginRight: 4 }} />
                            View / Enter Session
                          </button>
                          <button
                            onClick={() => handleEditHomeownerDashboard(homeowner)}
                            className="btn btn-secondary"
                            style={{ fontSize: 12, padding: '6px 12px' }}
                            title="Edit homeowner profile data, projects, and bids"
                          >
                            <FaEdit style={{ marginRight: 4 }} />
                            Edit Data
                          </button>
                          <button
                            onClick={() => handleDeleteHomeowner(homeowner)}
                            className="btn"
                            style={{ 
                              fontSize: 12, 
                              padding: '6px 12px',
                              backgroundColor: '#dc2626',
                              color: 'white',
                              border: 'none'
                            }}
                          >
                            <FaTrash style={{ marginRight: 4 }} />
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#6b7280' }}>
                  <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }}>👥</div>
                  <p style={{ margin: 0, fontWeight: '500' }}>No homeowners found</p>
                  <p style={{ margin: '8px 0 0 0', fontSize: 14, opacity: 0.8 }}>
                    {searchTerm ? 'Try adjusting your search criteria' : 'No homeowners have registered yet.'}
                  </p>
                  {!searchTerm && archiveFilter !== 'all' && (
                    <p style={{ margin: '12px 0 0 0', fontSize: 14 }}>
                      Try <button type="button" className="btn btn-secondary" style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => setArchiveFilter('all')}>Show All</button> to include archived.
                    </p>
                  )}
                  {!searchTerm && (
                    <button type="button" className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => onNavigate?.('admin-homeowner-add')}>
                      Add Homeowner +
                    </button>
                  )}
                </div>
              )}
            </SettingsCard>
          </>
        )}
      </div>
      
      {/* CB:BEGIN homeowner-documents-modal */}
      {selectedHomeownerForDocuments && (
        <HomeownerDocumentsModal
          homeownerId={selectedHomeownerForDocuments.id}
          homeownerName={
            selectedHomeownerForDocuments.firstName && selectedHomeownerForDocuments.lastName
              ? `${selectedHomeownerForDocuments.firstName} ${selectedHomeownerForDocuments.lastName}`
              : selectedHomeownerForDocuments.user?.email || undefined
          }
          isOpen={documentsModalOpen}
          onClose={() => {
            setDocumentsModalOpen(false);
            setSelectedHomeownerForDocuments(null);
          }}
        />
      )}
      {/* CB:END homeowner-documents-modal */}
    </ResponsiveContainer>
  );
};

export default AdminHomeownersList;
// CB:END admin-homeowners-list